import { ProjectLocationEditor } from "@components/project-location-editor/project-location-editor";
import { WorldMap } from "@components/world-map/world-map";
import {
  WorldMapButtonProps,
  WorldMapHandle,
} from "@components/world-map/world-map-types";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";
import { BaseProjectProps } from "@custom-types/project-types";
import { EditIcon } from "@flat-ui";
import { Skeleton } from "@mui/material";
import { ProjectMapEmptyPage } from "@pages/project-details/project-overview/project-map/project-map-empty-page";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { trackOpenProjectLocationEditor } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useRef, useState } from "react";

interface Props extends BaseProjectProps {
  /** Whether the component should display the loading skeleton */
  shouldShowSkeleton?: boolean;

  /** Whether the map allows interaction (e.g. editing coordinates). Defaults to true. */
  isInteractive?: boolean;
}

/**
 * Component that displays a map with the location of a single project.
 */
export function ProjectMap({
  project,
  shouldShowSkeleton = false,
  isInteractive = true,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const entities = isSdbProjectWithPosition(project) ? [project] : [];
  const mapRef = useRef<WorldMapHandle>(null);
  const positionRef = useRef<FaroCoreApiTypes.Position>(project.position);

  /** Handles opening the dialog */
  function openDialog(): void {
    trackOpenProjectLocationEditor({
      trackEvent,
      source: "project details page",
    });
    setIsDialogOpen(true);
  }

  /** Handles closing the dialog */
  function closeDialog(): void {
    setIsDialogOpen(false);
  }

  const buttons: WorldMapButtonProps[] = isInteractive
    ? [
        {
          icon: <EditIcon />,
          onClick: openDialog,
          tooltip: "Update coordinates",
        },
      ]
    : [];

  // Listens for updates in the project position and sets the map view to the new position.
  // Early exit if the new position has the same value as the cached/referenced position.
  useEffect(() => {
    if (
      project.position?.lat === positionRef.current?.lat &&
      project.position?.long === positionRef.current?.long
    ) {
      return;
    }

    positionRef.current = project.position;

    if (project.position) {
      mapRef.current?.goTo(project.position);
    }
  }, [project.position]);

  if (shouldShowSkeleton) {
    return <Skeleton variant="rectangular" height="100%" />;
  }

  if (!entities.length) {
    return (
      <ProjectMapEmptyPage
        project={project}
        isDialogOpen={isDialogOpen}
        openDialog={openDialog}
        closeDialog={closeDialog}
        isInteractive={isInteractive}
      />
    );
  }

  return (
    <>
      <WorldMap mapRef={mapRef} entities={entities} buttons={buttons} />
      {isInteractive && isDialogOpen && (
        <ProjectLocationEditor project={project} onClose={closeDialog} />
      )}
    </>
  );
}
